import React from "react";
import ReactDOM from "react-dom";
import styled, { ThemeProvider } from "styled-components";
import { placeholder, rgba, shade } from "polished";
import { Box, Flex } from "rebass";
import {
  Text,
  SearchIcon,
  CloseIcon,
  ArrowRightIcon,
  DropdownMenu,
} from "components";
import theme from "../theme";
import * as Icon from "react-feather";
import SearchResult from "./SearchResult";

const Wrapper = styled(Box)`
  position: relative;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  width: 44px;
  background: #fff;
  border-radius: 9999px;

  @keyframes wrapper-in {
    from {
      width: 44px;
      opacity: 0;
    }
    to {
      width: 100%;
      opacity: 1;
    }
  }

  &.is-visible {
    border-radius: 0;
    position: absolute;
    z-index: 1;
    animation: wrapper-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    animation-delay: 0s;
  }
  &.is-visible .search-search-button {
    position: absolute;
    top: ${(80 - 44) / 2}px;
    animation: icon-track-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    animation-delay: 0s;
    &:hover {
      > * {
        background-color: transparent !important;
      }
      svg > g > g {
        stroke: #363636;
      }
    }
  }
  &.is-visible .search-exit-button {
    animation: exit-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    animation-delay: 0.25s;
  }
  &.is-visible .search-input {
    display: block;
    animation: input-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    animation-delay: 0s;
  }
  &.is-visible .search-underline {
    animation: underline-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    animation-delay: 0.25s;
  }

  &.is-hidden {
    animation: wrapper-in 1s cubic-bezier(0.23, 1, 0.32, 1) backwards;
    animation-delay: 0s;
  }
  &.is-hidden .search-search-button {
    animation: icon-track-in 1s cubic-bezier(0.23, 1, 0.32, 1) backwards;
    animation-delay: 0s;
  }
  &.is-hidden .search-exit-button {
    animation: exit-in 1s cubic-bezier(0.23, 1, 0.32, 1) backwards;
    animation-delay: 0.25s;
  }
  &.is-hidden .search-input {
    animation: input-in 1s cubic-bezier(0.23, 1, 0.32, 1) backwards;
    animation-delay: 0s;
  }
  &.is-hidden .search-underline {
    animation: underline-in 1s cubic-bezier(0.23, 1, 0.32, 1) backwards;
    animation-delay: 0.25s;
  }
`;

const IconTrack = styled(Box)`
  position: relative;
  /* top: ${(80 - 44) / 2}px; */
  top: initial;
  right: 0;
  bottom: 0;
  z-index: 1;
  width: 44px;
  height: 44px;
  /* background: ${(props) => props.theme.colors.grey[1]}; */
  /* animation: icon-track-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards; */
  animation-delay: 1s;

  @keyframes icon-track-in {
    from {
      width: 44px;
      opacity: 1;
      pointer-events: none;
    }
    to {
      width: calc(100% - 24px + 10px);
      opacity: 0.5;
    }
  }
  @media (max-width: 40em) {
    &:not(:lang(ar)) {
      left: 16px;
    }
    &:lang(ar) {
      right: 16px;
    }
  }
`;

const SearchContainer = styled(Box)`
  position: relative;
  width: 44px;
  height: 44px;
  text-decoration: none;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  appearance: none;
  transition: background-color 500ms cubic-bezier(0.23, 1, 0.32, 1);

  svg {
    transition: stroke 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: stroke;
    stroke: ${(props) => props.theme.colors.grey[0]};
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.violet};
    svg {
      stroke: #fff;
    }
  }
  &:focus {
    background-color: ${(props) => props.theme.colors.violet};
    box-shadow: 0 0 0 4px
      ${(props) =>
        props.theme.colors.text && rgba(props.theme.colors.violet, 0.5)};
    outline: none;
    svg {
      stroke: #fff;
    }
  }
  &:active {
    background-color: ${(props) =>
      props.theme.colors.text && shade(0.875, props.theme.colors.violet)};
    svg {
      stroke: #fff;
    }
  }
`;
SearchContainer.defaultProps = {
  is: "button",
};

const SearchIconContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
`;

const SearchButton = (props) => (
  <IconTrack
    className={props.className}
    aria-label={props.ariaLabel}
    role={props.role}
  >
    <SearchContainer onClick={props.onClick}>
      <SearchIconContainer>
        <Icon.Search size={24} strokeWidth={1.5} />
      </SearchIconContainer>
    </SearchContainer>
  </IconTrack>
);

const ExitContainer = styled(Box)`
  position: absolute;
  top: ${(80 - 44) / 2}px;
  &:not(:lang(ar)) {
    right: 0;
  }
  &:lang(ar) {
    left: 0;
  }
  z-index: 3;
  width: 44px;
  height: 44px;
  text-decoration: none;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  appearance: none;
  transition: background-color 500ms cubic-bezier(0.23, 1, 0.32, 1);
  transform: scale(0);
  transform-origin: center;
  /* animation: exit-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards; */
  animation-delay: 1s;

  @keyframes exit-in {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }

  svg {
    stroke: ${(props) => props.theme.colors.grey[0]};
    transition: stroke 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: stroke;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.violet};
    svg {
      stroke: #fff;
    }
  }
  &:focus {
    background-color: ${(props) => props.theme.colors.violet};
    box-shadow: 0 0 0 4px
      ${(props) =>
        props.theme.colors.text && rgba(props.theme.colors.violet, 0.5)};
    outline: none;
    svg {
      stroke: #fff;
    }
  }
  &:active {
    background-color: ${(props) =>
      props.theme.colors.text && shade(0.875, props.theme.colors.violet)};
    svg {
      stroke: #fff;
    }
  }
  @media (max-width: 40em) {
    &:not(:lang(ar)) {
      right: 16px;
    }
    &:lang(ar) {
      left: 16px;
    }
  }
`;
ExitContainer.defaultProps = {
  is: "button",
};

const ExitButtonContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
`;

const ExitButton = (props) => (
  <ExitContainer
    className={props.className}
    onClick={props.onClick}
    role={props.role}
    aria-label={props.ariaLabel}
  >
    <ExitButtonContainer>
      <Icon.X size={24} strokeWidth={1.5} />
    </ExitButtonContainer>
  </ExitContainer>
);

const Input = styled.input`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: none;
  width: 100%;
  height: 100%;
  padding-right: ${48 + 8}px;
  padding-left: ${48 + 16}px;
  font-size: ${(props) => props.theme.fontSizes[3]}px;
  color: ${(props) => props.theme.colors.text};
  border: none;
  /* border-right: 1px solid ${(props) => props.theme.colors.grey[0]};
  border-left: 1px solid ${(props) => props.theme.colors.grey[0]}; */
  background: transparent;
  ${placeholder({ color: "rgba(0, 0, 0, 0.5)" })};
  transform: scaleX(0);
  transform-origin: right;
  /* animation: input-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards; */
  animation-delay: 1s;

  &:focus {
    outline: none;
    /* box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25); */
  }

  @keyframes input-in {
    from {
      transform: scaleX(0);
      opacity: 0;
    }
    to {
      transform: scaleX(1);
      opacity: 1;
    }
  }
  @media (max-width: 40em) {
    padding-right: ${48 + 16}px;
    padding-left: ${48 + 16}px;
  }
`;

const Underline = styled(Box)`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: ${(props) => props.theme.colors.grey[0]};
  transform: scaleX(0);
  transform-origin: right;
  /* animation: underline-in 1s cubic-bezier(0.23, 1, 0.32, 1) forwards; */
  animation-delay: 1s;

  @keyframes underline-in {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`;

const Results = styled(Box)`
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  z-index: 100;
  max-height: ${256}px;
  max-height: 50vh;
  overflow: scroll;
  background-color: ${(props) => props.theme.colors.grey[2]};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

class Search extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      results: [],
      value: "",
      longestValue: 0,
      searchIndex: 0,
      autocomplete: undefined,
    };
    this.searchRef = null;
    this.inputRef = null;
  }

  componentDidMount() {
    var algoliasearch = require("algoliasearch");
    var client = algoliasearch(
      "4AG971959W",
      "3b12490ed715fb5c60bb0a8bb6c11544"
    );
    var index = client.initIndex("primary_index");
    this.setState({ autocomplete: index });
  }

  setInputRef = (element) => {
    this.inputRef = ReactDOM.findDOMNode(element);
  };

  setSearchRef = (element) => {
    this.searchRef = ReactDOM.findDOMNode(element);
  };

  autocompleteUpdate = (event) => {
    if (event.target.value !== "") {
      this.getAutocomplete(event.target.value);
      //if (event.target.value.length > this.state.longestValue) {
      //this.setState({ longestValue: event.target.value.length });
      //}
    } else {
      this.setState({ results: [] });
    } //else if (this.state.longestValue > 3) {
    //this.props.toggleSearch();
    //}
    this.setState({ value: event.target.value });
  };

  handleKeyDown = (e) => {
    if (e.keyCode === 27) {
      this.props.toggleSearch();
    }
    if (e.keyCode === 13 && this.state.searchIndex === 0) {
      this.changeSearchIndex(1);
    }
    if (e.keyCode === 38) {
      this.changeSearchIndex(-1);
      e.stopPropagation();
      e.preventDefault();
    }
    if (e.keyCode === 40) {
      this.changeSearchIndex(1);
      e.stopPropagation();
      e.preventDefault();
    }
  };

  changeSearchIndex = (amount) => {
    if (
      this.state.searchIndex + amount !== this.state.results.length + 1 &&
      this.state.searchIndex + amount != -1
    ) {
      this.setState({ searchIndex: this.state.searchIndex + amount }, () => {
        if (this.state.searchIndex === 0) {
          this.inputRef.focus();
        } else {
          this.searchRef.childNodes[this.state.searchIndex].focus();
        }
      });
    } else {
      this.inputRef.focus();
    }
  };

  getAutocomplete = (change) => {
    this.state.autocomplete.search(
      change,
      {
        hitsPerPage: 20,
        filters: "lang:" + this.props.windowLang,
      },
      (err, content) => {
        if (err) {
          return;
        }
        this.setState({ results: content.hits, searchIndex: 0 });
      }
    );
  };

  render() {
    return (
      <ThemeProvider theme={theme}>
        <Wrapper
          className={
            this.props.searchOpen
              ? "is-visible"
              : this.props.searchHasHappened
              ? ""
              : ""
          }
        >
          <SearchButton
            ariaLabel="Open search"
            role="button"
            className="search-search-button"
            onClick={() => {
              this.props.toggleSearch();
              this.setState({ value: "", results: [] });
            }}
          />
          {this.props.searchOpen && (
            <ExitButton
              ariaLabel="Exit search"
              role="button"
              className="search-exit-button"
              onClick={() => {
                this.props.toggleSearch();
                this.setState({ value: "", results: [] });
              }}
            />
          )}
          {this.props.searchOpen && (
            <Input
              aria-label="Search"
              aria-autocomplete="list"
              aria-controls="autocomplete-results"
              role="combobox"
              className="search-input"
              autoFocus="autofocus"
              placeholder={
                this.props.windowLang === "en-us"
                  ? "Search..."
                  : this.props.windowLang === "es-mx"
                  ? "Buscar..."
                  : "بحث"
              }
              value={this.state.value}
              onChange={this.autocompleteUpdate}
              onKeyDown={this.handleKeyDown}
              ref={this.setInputRef}
            />
          )}
          {/* <Underline className="search-underline" /> */}
          {this.props.searchOpen && (
            <Results
              ref={this.setSearchRef}
              id="autocomplete-results"
              role="listbox"
            >
              {this.state.value !== "" && (
                <SearchResult
                  result={{ name: "", type: "Algolia" }}
                  onClick={() => {
                    this.props.toggleSearch();
                    this.setState({
                      value: "",
                      results: [],
                      longestValue: 0,
                    });
                  }}
                  onKeyDown={this.handleKeyDown}
                />
              )}
              {this.state.results.length === 0 && this.state.value !== "" && (
                <SearchResult
                  result={{
                    name:
                      this.props.windowLang === "en-us"
                        ? "No results found"
                        : this.props.windowLang === "es-mx"
                        ? "No se encuentra resultados"
                        : "لم يتم العثور على اي نتائج",
                    type: "noresults",
                  }}
                  onClick={() => {
                    this.props.toggleSearch();
                    this.setState({
                      value: "",
                      results: [],
                      longestValue: 0,
                    });
                  }}
                  onKeyDown={this.handleKeyDown}
                />
              )}
              {this.state.results.map((result, index) => (
                <SearchResult
                  key={"result" + index}
                  result={{ ...result, index: index }}
                  windowLang={this.props.windowLang}
                  onClick={() => {
                    this.props.toggleSearch();
                    this.setState({
                      value: "",
                      results: [],
                      longestValue: 0,
                    });
                  }}
                  onKeyDown={this.handleKeyDown}
                />
              ))}
            </Results>
          )}
        </Wrapper>
      </ThemeProvider>
    );
  }
}

export default Search;



// WEBPACK FOOTER //
// ./src/components/Search.js